<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import {
  Plus,
  Search,
  X,
  CalendarRange,
  User,
  CircleOff,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-vue-next';
import {
  Button,
  Input,
  ScrollArea,
  Skeleton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RangeCalendar,
} from '@manta/ui-kit';
import { CalendarDate } from '@internationalized/date';
import { useI18n } from 'vue-i18n';
import { useSessionsInfiniteQuery } from '@/api/sessions/useSessionsInfiniteQuery';
import { useSessionCreateMutation } from '@/api/sessions/useSessionCreateMutation';
import type { ScribeSession } from '@/types';
import SessionListItem from '@/components/SessionListItem.vue';
import FacetedFilter from '@/components/FacetedFilter.vue';

const { t } = useI18n();
const router = useRouter();

const STATUS_ORDER = [
  'draft',
  'recording',
  'processing',
  'ready',
  'completed',
  'error',
] as const;

// ── Filter state ────────────────────────────────────────────────────────────
const searchInput = ref('');
const searchTerm = ref('');
const statusFilter = ref<string[]>([]);
const clientFilter = ref<string[]>([]);
type DateRange = { start?: CalendarDate; end?: CalendarDate };
const dateRange = ref<DateRange>({});

// Debounce the free-text search so we don't hit the API on every keystroke.
let searchTimer: ReturnType<typeof setTimeout> | undefined;
watch(searchInput, (value) => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    searchTerm.value = value;
  }, 300);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

// A single selected customer facet maps to a server filter; selecting both (or
// none) means "no filter".
const clientParam = computed<'linked' | 'unlinked' | null>(() =>
  clientFilter.value.length === 1
    ? (clientFilter.value[0] as 'linked' | 'unlinked')
    : null,
);

function dayStartIso(c?: CalendarDate): string | null {
  if (!c) return null;
  return new Date(c.year, c.month - 1, c.day, 0, 0, 0, 0).toISOString();
}
function dayEndIso(c?: CalendarDate): string | null {
  if (!c) return null;
  return new Date(c.year, c.month - 1, c.day, 23, 59, 59, 999).toISOString();
}

const createdAtFrom = computed(() => dayStartIso(dateRange.value.start));
const createdAtTo = computed(() =>
  dayEndIso(dateRange.value.end ?? dateRange.value.start),
);

// ── Query (server-side filtering + pagination) ──────────────────────────────
const {
  data,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
} = useSessionsInfiniteQuery({
  search: searchTerm,
  status: statusFilter,
  client: clientParam,
  createdAtFrom,
  createdAtTo,
});

const sessions = computed<ScribeSession[]>(
  () => data.value?.pages.flatMap((p) => p.data) ?? [],
);
const totalCount = computed(() => data.value?.pages[0]?.meta.totalCount ?? 0);

const createMutation = useSessionCreateMutation();

// ── Filter option config ────────────────────────────────────────────────────
const statusOptions = computed(() =>
  STATUS_ORDER.map((value) => ({
    value,
    label: t(`sessions.status.${value}`),
  })),
);
const clientOptions = computed(() => [
  { value: 'linked', label: t('sessions.client.linked'), icon: User },
  { value: 'unlinked', label: t('sessions.client.unlinked'), icon: CircleOff },
]);

const isFiltered = computed(
  () =>
    !!searchInput.value.trim() ||
    statusFilter.value.length > 0 ||
    clientFilter.value.length > 0 ||
    !!dateRange.value.start ||
    !!dateRange.value.end,
);

function resetFilters() {
  searchInput.value = '';
  searchTerm.value = '';
  statusFilter.value = [];
  clientFilter.value = [];
  dateRange.value = {};
}

const dateLabel = computed(() => {
  const { start, end } = dateRange.value;
  if (!start && !end) return '';
  const fmt = (c?: CalendarDate) =>
    c
      ? new Date(c.year, c.month - 1, c.day).toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit',
        })
      : '…';
  return start && end && start.compare(end) === 0
    ? fmt(start)
    : `${fmt(start)} – ${fmt(end)}`;
});

// ── Grouping (display only; server already returns newest-first) ─────────────
type Group = { key: string; label: string; items: ScribeSession[] };

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const grouped = computed<Group[]>(() => {
  const today = startOfDay(new Date());
  const yesterday = today - 24 * 60 * 60 * 1000;

  const groups: Record<string, ScribeSession[]> = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  for (const s of sessions.value) {
    const day = startOfDay(new Date(s.created_at));
    if (day === today) groups.today.push(s);
    else if (day === yesterday) groups.yesterday.push(s);
    else groups.earlier.push(s);
  }

  return [
    { key: 'today', label: t('sessions.groups.today'), items: groups.today },
    {
      key: 'yesterday',
      label: t('sessions.groups.yesterday'),
      items: groups.yesterday,
    },
    {
      key: 'earlier',
      label: t('sessions.groups.earlier'),
      items: groups.earlier,
    },
  ].filter((g) => g.items.length > 0);
});

// Collapsible day groups (all expanded by default).
const collapsedGroups = ref<Set<string>>(new Set());
function toggleGroup(key: string) {
  const next = new Set(collapsedGroups.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedGroups.value = next;
}
const isCollapsed = (key: string) => collapsedGroups.value.has(key);

async function createSession() {
  const created = await createMutation.mutateAsync({ status: 'draft' });
  void router.push({ name: 'session-detail', params: { id: created.id } });
}
</script>

<template>
  <aside class="flex w-80 shrink-0 flex-col border-r bg-background">
    <div class="flex items-center justify-between px-4 py-3">
      <h1 class="text-base font-semibold">{{ t('sessions.title') }}</h1>
      <Button
        size="sm"
        :disabled="createMutation.isPending.value"
        @click="createSession"
      >
        <Plus class="mr-1 h-4 w-4" />
        {{ t('sessions.new') }}
      </Button>
    </div>

    <div class="space-y-2 px-4 pb-3">
      <div class="relative">
        <Search
          class="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
        />
        <Input
          v-model="searchInput"
          :placeholder="t('sessions.search')"
          class="pl-8 pr-8"
        />
        <button
          v-if="searchInput"
          type="button"
          class="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
          @click="searchInput = ''"
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <FacetedFilter
          v-model="statusFilter"
          :title="t('sessions.filters.status')"
          :options="statusOptions"
        />
        <FacetedFilter
          v-model="clientFilter"
          :title="t('sessions.filters.client')"
          :options="clientOptions"
        />

        <Popover>
          <PopoverTrigger as-child>
            <Button
              variant="outline"
              size="sm"
              class="h-8"
              :class="dateLabel ? 'border-solid' : 'border-dashed'"
            >
              <CalendarRange class="mr-2 h-4 w-4" />
              {{ dateLabel || t('sessions.filters.date') }}
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-auto p-0" align="start">
            <RangeCalendar
              :model-value="dateRange as any"
              @update:model-value="(v: any) => (dateRange = v ?? {})"
            />
            <div class="flex justify-end border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                :disabled="!dateRange.start && !dateRange.end"
                @click="dateRange = {}"
              >
                {{ t('sessions.filters.clearDate') }}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          v-if="isFiltered"
          variant="ghost"
          size="sm"
          class="h-8 px-2"
          @click="resetFilters"
        >
          {{ t('sessions.filters.reset') }}
          <X class="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>

    <ScrollArea class="flex-1">
      <div class="px-2 pb-4">
        <template v-if="isLoading">
          <div v-for="n in 6" :key="n" class="px-2 py-2">
            <Skeleton class="mb-2 h-4 w-3/4" />
            <Skeleton class="h-3 w-1/2" />
          </div>
        </template>

        <div
          v-else-if="isError"
          class="px-3 py-6 text-sm text-muted-foreground"
        >
          {{ t('sessions.loadError') }}
        </div>

        <div v-else-if="sessions.length === 0" class="px-3 py-10 text-center">
          <p class="text-sm font-medium">
            {{ isFiltered ? t('sessions.noMatches') : t('sessions.empty') }}
          </p>
          <p class="mt-1 text-xs text-muted-foreground">
            {{
              isFiltered ? t('sessions.noMatchesHint') : t('sessions.emptyHint')
            }}
          </p>
        </div>

        <template v-else>
          <div v-for="group in grouped" :key="group.key" class="mb-2">
            <button
              type="button"
              class="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              @click="toggleGroup(group.key)"
            >
              <ChevronDown
                v-if="!isCollapsed(group.key)"
                class="h-4 w-4 shrink-0 text-muted-foreground"
              />
              <ChevronRight
                v-else
                class="h-4 w-4 shrink-0 text-muted-foreground"
              />
              <span>{{ group.label }}</span>
            </button>
            <template v-if="!isCollapsed(group.key)">
              <SessionListItem
                v-for="session in group.items"
                :key="session.id"
                :session="session"
              />
            </template>
          </div>

          <div v-if="hasNextPage" class="px-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              class="w-full"
              :disabled="isFetchingNextPage"
              @click="fetchNextPage()"
            >
              <Loader2
                v-if="isFetchingNextPage"
                class="mr-2 h-4 w-4 animate-spin"
              />
              {{ t('sessions.loadMore', { count: totalCount }) }}
            </Button>
          </div>
        </template>
      </div>
    </ScrollArea>
  </aside>
</template>
